import React, { useCallback, useMemo, useState } from 'react';
import amplitudeLog from '@client/amplitude';
import {
  AMP_PRODUCT_EPD_IMPORT,
  MAX_FILES_UPLOAD_BYTES,
} from '@shared/constants';
import { Box, CircularProgress, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { FileRejection, useDropzone } from 'react-dropzone';
import axios from 'axios';
import { EpdExtraction } from '@shared/helpers/epd-extraction-json-schema.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { resolveEpdPdfDrop } from './epd-pdf-drop.helpers';

const EPD_PDF_ACCEPT = { 'application/pdf': ['.pdf'] } as const;

type EPDPdfDropzoneProps = {
  onPopulate: (parsed: EpdExtraction) => void;
};

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  root: {
    alignItems: 'center',
    borderColor: '#0c7b83',
    borderRadius: spacing(0.5),
    borderStyle: 'dashed',
    borderWidth: 2,
    color: '#0c7b83',
    display: 'flex',
    height: 220,
    justifyContent: 'center',
    textAlign: 'center',
    userSelect: 'none',
  },
}));

export const EPDPdfDropzone: React.FC<EPDPdfDropzoneProps> = ({
  onPopulate,
}) => {
  const { classes } = useStyles();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setError(null);
      const dropped = resolveEpdPdfDrop(acceptedFiles, fileRejections);
      switch (dropped.type) {
        case 'rejected':
          setError(dropped.message);
          return;
        case 'accepted':
          break;
        default:
          return assertUnreachable(dropped);
      }
      const file = dropped.file;

      setLoading(true);

      try {
        const form = new FormData();
        form.append('file', file);
        const { data } = await axios.post<EpdExtraction>(
          '/files/epd/analyze',
          form,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          },
        );

        onPopulate(data);
        amplitudeLog(AMP_PRODUCT_EPD_IMPORT, { 'Extracted Name': data.name });
      } catch (err) {
        if (axios.isAxiosError(err)) {
          const message =
            typeof err.response?.data?.error === 'string'
              ? err.response.data.error
              : err.message;
          setError(message || 'Could not process the PDF');
        } else {
          setError('Could not process the PDF');
        }
      } finally {
        setLoading(false);
      }
    },
    [onPopulate],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: EPD_PDF_ACCEPT,
    maxSize: MAX_FILES_UPLOAD_BYTES,
    disabled: loading,
    onDrop,
  });

  const content = useMemo(() => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">Analyzing EPD data</Typography>
        </Box>
      );
    }
    if (error) {
      return (
        <Typography
          variant="subtitle2"
          color="error"
          sx={{
            textTransform: 'none',
          }}
        >
          {error}
        </Typography>
      );
    }
    return (
      <Typography
        variant="subtitle2"
        sx={{
          textTransform: 'none',
        }}
      >
        {isDragActive ? 'Drop the PDF to import' : 'Drop an EPD here to import'}
      </Typography>
    );
  }, [error, isDragActive, loading]);

  return (
    <div {...getRootProps({ className: classes.root })}>
      <input {...getInputProps()} />
      {content}
    </div>
  );
};

export default EPDPdfDropzone;
